import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, User } from "lucide-react";
import { toast } from "sonner";

/**
 * Global gate that forces clients to set a full name AND phone number on
 * their profile before continuing to use the app. Triggered after sign-in
 * if either field is missing/blank.
 *
 * Skipped on the auth/reset/confirm/admin routes to avoid blocking those flows.
 */
const SKIP_PATHS = ["/auth", "/signup", "/reset-password", "/confirm-client", "/sms-consent", "/delete-account"];

// Phone numbers used as placeholder logins for phone-only signups.
const isPlaceholderEmail = (email?: string | null) =>
  !!email && email.toLowerCase().endsWith("@phone.inparadisebeauty.local");

const normalizePhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  // If 10 digits, assume US and prefix +1. If already starts with country code, prefix +.
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
};

const isValidPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

export const RequireNameDialog = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [needName, setNeedName] = useState(false);
  const [needPhone, setNeedPhone] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSkipPath =
    SKIP_PATHS.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.startsWith("/admin");

  useEffect(() => {
    if (loading || !user || onSkipPath) {
      setOpen(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, phone")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;
      const currentName = (data?.full_name || "").trim();
      const currentPhone = (data?.phone || "").trim();

      // Phone-only signups already have a phone in their email placeholder —
      // their phone is captured at signup, so don't re-prompt them.
      const phoneOnlyAccount = isPlaceholderEmail(user.email);

      const missingName = currentName.length < 2;
      const missingPhone = !phoneOnlyAccount && !isValidPhone(currentPhone);

      if (missingName || missingPhone) {
        setNeedName(missingName);
        setNeedPhone(missingPhone);
        setName("");
        setPhone("");
        setOpen(true);
      } else {
        setOpen(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, location.pathname, onSkipPath]);

  const handleSave = async () => {
    if (!user) return;

    const updates: { full_name?: string; phone?: string } = {};

    if (needName) {
      const trimmed = name.trim();
      if (trimmed.length < 2) {
        toast.error("Please enter your full name");
        return;
      }
      updates.full_name = trimmed;
    }

    if (needPhone) {
      if (!isValidPhone(phone)) {
        toast.error("Please enter a valid phone number");
        return;
      }
      updates.phone = normalizePhone(phone);
    }

    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error("Failed to save your details. Please try again.");
      return;
    }
    toast.success("Thanks! Your profile is up to date.");
    setOpen(false);
  };

  if (!user || onSkipPath) return null;

  const description = needName && needPhone
    ? "We need your full name and phone number on file before you continue. This helps us personalize your bookings and send appointment reminders."
    : needName
      ? "We need your full name on file before you continue. This helps us personalize your bookings and reminders."
      : "We need a phone number on file before you continue. This is how we send appointment reminders and updates.";

  return (
    <Dialog open={open} onOpenChange={() => { /* required — cannot dismiss */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-serif italic flex items-center gap-2">
            <User size={18} className="text-primary" /> One quick thing…
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {needName && (
            <div className="space-y-2">
              <Label htmlFor="require-name">Full Name <span className="text-destructive">*</span></Label>
              <Input
                id="require-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                autoFocus
                maxLength={100}
                onKeyDown={(e) => { if (e.key === "Enter" && !needPhone) handleSave(); }}
              />
            </div>
          )}
          {needPhone && (
            <div className="space-y-2">
              <Label htmlFor="require-phone">Phone Number <span className="text-destructive">*</span></Label>
              <Input
                id="require-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                autoFocus={!needName}
                maxLength={20}
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
            {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : "Save & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequireNameDialog;
